/**
 * Finnish grammatical forms for destination names.
 *
 * Finnish uses case endings instead of prepositions, so "This Week In Helsinki"
 * must become "Tällä viikolla Helsingissä" — not "kohteessa Helsinki".
 *
 * - `in`: inessive/adessive ("in <city>")   → Helsingissä, Tampereella
 * - `to`: illative/allative ("to <city>")   → Helsinkiin, Tampereelle
 *
 * Unknown cities fall back to the neutral "kohteessa <City>" / "kohteeseen
 * <City>" wording, which is always grammatically safe.
 */

type CityForms = { in: string; to: string };

const FI_CITY_FORMS: Record<string, CityForms> = {
  helsinki: { in: "Helsingissä", to: "Helsinkiin" },
  hanko: { in: "Hangossa", to: "Hankoon" },
  tampere: { in: "Tampereella", to: "Tampereelle" },
  turku: { in: "Turussa", to: "Turkuun" },
  rovaniemi: { in: "Rovaniemellä", to: "Rovaniemelle" },
  espoo: { in: "Espoossa", to: "Espooseen" },
  vantaa: { in: "Vantaalla", to: "Vantaalle" },
  oulu: { in: "Oulussa", to: "Ouluun" },
  kuopio: { in: "Kuopiossa", to: "Kuopioon" },
  lahti: { in: "Lahdessa", to: "Lahteen" },
  jyvaskyla: { in: "Jyväskylässä", to: "Jyväskylään" },
  porvoo: { in: "Porvoossa", to: "Porvooseen" },
  savonlinna: { in: "Savonlinnassa", to: "Savonlinnaan" },
  vaasa: { in: "Vaasassa", to: "Vaasaan" },
  pori: { in: "Porissa", to: "Poriin" },
  kotka: { in: "Kotkassa", to: "Kotkaan" },
  levi: { in: "Levillä", to: "Leville" },
  saariselka: { in: "Saariselällä", to: "Saariselälle" },
  inari: { in: "Inarissa", to: "Inariin" },
  naantali: { in: "Naantalissa", to: "Naantaliin" },
};

function normalizeSlug(slug: string): string {
  return slug
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

/** "in <city>" — Finnish inessive/adessive, English falls back to the name. */
export function cityInForm(lang: string, slug: string, name: string): string {
  if (lang !== "fi") return name;
  const f = FI_CITY_FORMS[normalizeSlug(slug)];
  return f ? f.in : `kohteessa ${name}`;
}

/** "to <city>" — Finnish illative/allative, English falls back to the name. */
export function cityToForm(lang: string, slug: string, name: string): string {
  if (lang !== "fi") return name;
  const f = FI_CITY_FORMS[normalizeSlug(slug)];
  return f ? f.to : `kohteeseen ${name}`;
}
